import { SubmitHandler } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Label } from '@givebutter/primitives/label';
import { useToast } from '@givebutter/primitives/toast';
import { updateCampaign } from 'api/campaigns';
import { deleteCampaignMediaByUrl, uploadCampaignMedia } from 'api/media';
import * as Form from 'components/form';
import * as SettingsCard from 'components/settings-card';
import { Upload } from 'components/upload';
import { CAMPAIGN_SEO_LINK } from 'constants/links';
import { Settings } from 'constants/settings';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface SharingSeoFormValues {
    title: string;
    description: string;
}

const CampaignSharingSeo = () => {
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();
    const { data: campaign } = useCurrentCampaign();
    const campaignId = campaign?.id!;
    const methods = Form.useForm<SharingSeoFormValues>({
        defaultValues: {
            title: campaign?.metaTitle?.value ?? '',
            description: campaign?.metaDescription?.value ?? '',
        },
    });
    const { handleSubmit, control, path } = methods;

    const { mutate: submit } = useMutation({
        mutationFn: (values: SharingSeoFormValues) => {
            const data = {
                settings: [
                    {
                        code: Settings.MetaTitle.Code,
                        value: values.title,
                        active: values.title.length > 0,
                    },
                    {
                        code: Settings.MetaDescription.Code,
                        value: values.description,
                        active: values.description.length > 0,
                    },
                ],
            };

            return updateCampaign({ campaignId: campaignId, data });
        },

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.campaign(campaignId),
            });
            toast.success('Updated SEO settings!');
        },
        onError: (e: GivebutterAxiosError) =>
            toast.error(e.response?.data?.message ?? 'Failed to update SEO settings'),
    });

    const { mutate: deletePhoto } = useMutation({
        mutationFn: (url: string) => deleteCampaignMediaByUrl(campaignId, url),
        onSuccess: () => {
            toast.success('Updated SEO photo!');
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.campaign(campaignId),
            });
        },
        onError: (e: GivebutterAxiosError) =>
            toast.error(e.response?.data?.message ?? 'Failed to update SEO photo'),
    });

    return (
        <Form.Provider {...methods}>
            <SettingsCard.Root>
                <SettingsCard.LeftSide>
                    <SettingsCard.Heading>SEO settings</SettingsCard.Heading>
                    <p className={'text-sm text-gray-700'}>
                        Manage your campaign's advanced SEO sharing settings.{' '}
                        <a target="_blank" rel="noopener noreferrer" href={CAMPAIGN_SEO_LINK}>
                            Read the guide
                        </a>
                        .
                    </p>
                </SettingsCard.LeftSide>
                <SettingsCard.RightSide>
                    <form onSubmit={handleSubmit(submit as SubmitHandler<SharingSeoFormValues>)}>
                        <div className={'flex flex-col gap-4'}>
                            <Form.Container>
                                <Form.Label className={'text-base'} name={path('title')}>
                                    SEO title
                                </Form.Label>
                                <p className={'text-sm text-gray-700'}>
                                    This is the title used on Google and when sharing on social
                                    media.
                                </p>
                                <Form.TextInputRoot control={control} name={'title'}>
                                    <Form.TextInputForm placeholder={campaign?.title} />
                                </Form.TextInputRoot>
                            </Form.Container>
                            <Form.Container>
                                <Form.Label className={'text-base'} name={path('description')}>
                                    SEO description
                                </Form.Label>
                                <p className={'text-sm text-gray-700'}>
                                    This is the description used on Google and when sharing on
                                    social media.
                                </p>
                                <Form.TextInputRoot control={control} name={'description'}>
                                    <Form.TextInputForm placeholder={'Optional'} />
                                </Form.TextInputRoot>
                            </Form.Container>
                            <Form.Container>
                                <Label className={'text-base'}>SEO image</Label>
                                <div className="flex flex-col gap-2">
                                    <p className={'text-sm text-gray-700'}>
                                        This is the image used when sharing on social media. If not
                                        set, the image will default to your account's{' '}
                                        <Link to={`/accounts/${accountId}/settings/about/branding`}>
                                            cover photo
                                        </Link>
                                        .
                                    </p>
                                    {campaign?.meta.image &&
                                        campaign.meta.imageType === 'cover' && (
                                            <div>
                                                <img
                                                    src={campaign.meta.image}
                                                    alt="account cover"
                                                    className="max-h-40"
                                                />
                                            </div>
                                        )}
                                    <Upload
                                        className={'[&_.filepond--item]:!w-full'}
                                        defaultFiles={
                                            campaign?.meta.image &&
                                            campaign.meta.imageType === 'meta'
                                                ? [{ id: 'seo', url: campaign.meta.image }]
                                                : []
                                        }
                                        acceptedFileTypes={['image/*']}
                                        allowReorder={false}
                                        maxFiles={1}
                                        strategy={'remote'}
                                        uploadFn={async file => {
                                            const { data } = await uploadCampaignMedia(
                                                campaignId,
                                                file,
                                                'meta',
                                            );

                                            return data.url;
                                        }}
                                        onremovefile={(_, file) =>
                                            deletePhoto(file.getMetadata('url'))
                                        }
                                    />
                                </div>
                            </Form.Container>
                            <div>
                                <Form.SubmitButton>Save</Form.SubmitButton>
                            </div>
                        </div>
                    </form>
                </SettingsCard.RightSide>
            </SettingsCard.Root>
        </Form.Provider>
    );
};

export { CampaignSharingSeo };
